import {
  ExpandOutlined,
  RedoOutlined,
  ReloadOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { Button, Modal, Segmented, Space, Tooltip, message } from 'antd';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';

const ASPECT_OPTIONS = [
  { label: 'Free', value: 'free' },
  { label: '1:1', value: '1' },
  { label: '4:3', value: '1.3333333333333333' },
  { label: '3:4', value: '0.75' },
  { label: '16:9', value: '1.7777777777777777' },
];

// Cap the exported canvas so an over-sized source cannot blow up memory.
const MAX_OUTPUT_SIZE = 4096;
const JPEG_QUALITY = 0.92;

/**
 * Cropper.js based crop dialog. The user drags a rectangle over the image to
 * pick the area to keep — the same interaction as a screenshot/snipping tool.
 * Confirming with no selection keeps the whole (possibly rotated/zoomed) image.
 */
const ImageCropModal = ({
  open,
  src,
  fileName,
  fileType,
  onCancel,
  onConfirm,
}) => {
  const imageRef = useRef(null);
  const cropperRef = useRef(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [aspect, setAspect] = useState('free');
  const [exporting, setExporting] = useState(false);

  // Tear the instance down if the dialog is unmounted without cancel/confirm.
  useEffect(
    () => () => {
      cropperRef.current?.destroy();
      cropperRef.current = null;
    },
    [],
  );

  // Cropper needs the <img> laid out, so build it once the image data is in.
  const handleImageLoad = () => {
    if (!imageRef.current) return;
    cropperRef.current?.destroy();
    cropperRef.current = new Cropper(imageRef.current, {
      viewMode: 1,
      dragMode: 'crop',
      // No initial crop box: dragging on the image starts a fresh selection.
      autoCrop: false,
      responsive: true,
      restore: true,
      checkOrientation: true,
      guides: true,
      center: true,
      highlight: true,
      background: true,
      toggleDragModeOnDblclick: false,
      minCropBoxWidth: 8,
      minCropBoxHeight: 8,
      crop: (event) => {
        const { width, height } = event.detail;
        setHasSelection(width > 1 && height > 1);
      },
    });
  };

  const destroyCropper = () => {
    cropperRef.current?.destroy();
    cropperRef.current = null;
    setHasSelection(false);
    setAspect('free');
  };

  const handleAspectChange = (value) => {
    const next = String(value);
    setAspect(next);
    const cropper = cropperRef.current;
    if (!cropper) return;
    cropper.setAspectRatio(next === 'free' ? NaN : Number(next));
  };

  const selectWholeImage = () => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    cropper.crop();
    const canvas = cropper.getCanvasData();
    cropper.setCropBoxData({
      left: canvas.left,
      top: canvas.top,
      width: canvas.width,
      height: canvas.height,
    });
  };

  const resetAll = () => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    cropper.reset();
    cropper.clear();
    cropper.setAspectRatio(NaN);
    setAspect('free');
    setHasSelection(false);
  };

  const handleCancel = () => {
    destroyCropper();
    onCancel();
  };

  const handleOk = () => {
    const cropper = cropperRef.current;
    if (!cropper) return;

    // JPEG has no alpha channel, so anything that is not a JPEG is exported as
    // PNG to avoid transparent pixels turning black.
    const outputType = fileType === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    const canvas = cropper.getCroppedCanvas({
      maxWidth: MAX_OUTPUT_SIZE,
      maxHeight: MAX_OUTPUT_SIZE,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
      fillColor: outputType === 'image/jpeg' ? '#fff' : undefined,
    });

    if (!canvas) {
      message.error('Could not crop this image.');
      return;
    }

    setExporting(true);
    canvas.toBlob(
      (blob) => {
        setExporting(false);
        if (!blob) {
          message.error('Could not crop this image.');
          return;
        }
        const cropped = new File([blob], fileName, {
          type: outputType,
          lastModified: Date.now(),
        });
        destroyCropper();
        onConfirm(cropped);
      },
      outputType,
      JPEG_QUALITY,
    );
  };

  return (
    <Modal
      open={open}
      title="Crop Image"
      width={880}
      maskClosable={false}
      destroyOnHidden
      onCancel={handleCancel}
      onOk={handleOk}
      okText="Crop"
      cancelText="Cancel"
      okButtonProps={{ loading: exporting }}
      afterClose={destroyCropper}
    >
      <div className="tw-flex tw-flex-col tw-gap-2">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <Segmented
            size="small"
            value={aspect}
            options={ASPECT_OPTIONS}
            onChange={handleAspectChange}
          />
          <Space size={4}>
            <Tooltip title="Rotate left">
              <Button
                size="small"
                icon={<UndoOutlined />}
                onClick={() => cropperRef.current?.rotate(-90)}
              />
            </Tooltip>
            <Tooltip title="Rotate right">
              <Button
                size="small"
                icon={<RedoOutlined />}
                onClick={() => cropperRef.current?.rotate(90)}
              />
            </Tooltip>
            <Tooltip title="Zoom in">
              <Button
                size="small"
                icon={<ZoomInOutlined />}
                onClick={() => cropperRef.current?.zoom(0.1)}
              />
            </Tooltip>
            <Tooltip title="Zoom out">
              <Button
                size="small"
                icon={<ZoomOutOutlined />}
                onClick={() => cropperRef.current?.zoom(-0.1)}
              />
            </Tooltip>
            <Tooltip title="Select whole image">
              <Button
                size="small"
                icon={<ExpandOutlined />}
                onClick={selectWholeImage}
              />
            </Tooltip>
            <Tooltip title="Reset">
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={resetAll}
              />
            </Tooltip>
          </Space>
        </div>

        <div
          style={{ height: 460, background: '#f5f5f5' }}
          className="tw-overflow-hidden"
        >
          <img
            ref={imageRef}
            src={src}
            alt="Crop preview"
            onLoad={handleImageLoad}
            style={{ maxWidth: '100%', display: 'block' }}
          />
        </div>

        <div className="tw-text-xs tw-text-gray-500">
          {hasSelection
            ? 'Drag the handles to fine-tune the selection, or drag a new rectangle to start over.'
            : 'Drag across the image to select the area you want — like taking a screenshot. Without a selection the full image is kept.'}
        </div>
      </div>
    </Modal>
  );
};

ImageCropModal.propTypes = {
  open: PropTypes.bool,
  src: PropTypes.string,
  fileName: PropTypes.string,
  fileType: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

/**
 * Queues files through {@link ImageCropModal} one at a time and resolves with
 * the cropped `File` (or `null` when the user cancels). Intended to be awaited
 * inside antd `Upload.beforeUpload`.
 *
 * ```jsx
 * const { cropImage, cropModal } = useImageCrop();
 * // ...
 * beforeUpload={async (file) => {
 *   const cropped = await cropImage(file);
 *   if (!cropped) return Upload.LIST_IGNORE;
 *   return cropped;
 * }}
 * ```
 */
export const useImageCrop = () => {
  const [request, setRequest] = useState(null);
  const activeRef = useRef(null);
  const queueRef = useRef([]);

  const enqueue = useCallback((next) => {
    if (activeRef.current) {
      queueRef.current.push(next);
      return;
    }
    activeRef.current = next;
    setRequest(next);
  }, []);

  const settle = useCallback((result) => {
    activeRef.current?.resolve(result);
    const next = queueRef.current.shift() ?? null;
    activeRef.current = next;
    setRequest(next);
  }, []);

  const cropImage = useCallback(
    (file) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () =>
          enqueue({ file, src: String(reader.result), resolve });
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      }),
    [enqueue],
  );

  const cropModal = request ? (
    <ImageCropModal
      // Remount per file so Cropper re-initialises on the new image.
      key={request.file.uid ?? request.file.name}
      open
      src={request.src}
      fileName={request.file.name}
      fileType={request.file.type}
      onCancel={() => settle(null)}
      onConfirm={(cropped) => {
        // antd tracks the upload by `uid`; carry it over to the cropped file.
        cropped.uid = request.file.uid;
        settle(cropped);
      }}
    />
  ) : null;

  return { cropImage, cropModal };
};

export default ImageCropModal;
